import type { CartItemType } from '@/store/types/cart'
import type { CustomerType } from '@/store/types/user'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import type { CosmicVariationType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { mixpanelService } from '../initializeMixPanel'
import { MixpanelEvent } from '../mixpanelEvent'
import type {
  HydraGetSubscriptionItem,
  HydraLinesItem,
} from '../../Hydra/account/GET/getSubscriptions'

type TrackSubscriptionErrorParams = {
  subscriptionId: string
  responseCode?: string
  errorMessage?: string
}

export const TrackSubscriptionError = ({
  subscriptionId,
  responseCode,
  errorMessage,
}: TrackSubscriptionErrorParams) =>
  mixpanelService.trackEvent(
    MixpanelEvent.REVIEW_SUBSCRIPTION_CHANGES_SERVER_ERROR,
    {
      subscription_id: subscriptionId,
      response_code: responseCode || '',
      error_message: errorMessage || '',
    },
  )

type TrackSubscriptionSelectedParams = {
  scenario: 'existing' | 'dismiss' | 'new'
  subscriptionId?: string
}

export const TrackSubscriptionSelected = ({
  scenario,
  subscriptionId,
}: TrackSubscriptionSelectedParams) =>
  mixpanelService.trackEvent(
    MixpanelEvent.CREATE_OR_COMBINE_SUBSCRIPTION_SELECTED,
    {
      scenario,
      ...(subscriptionId && { subscription_id: subscriptionId }),
    },
  )

export const TrackSubscriptionModalAppears = () =>
  mixpanelService.trackEvent(
    MixpanelEvent.CREATE_OR_COMBINE_SUBSCRIPTION_APPEARS,
    {},
  )

type TrackSubscriptionCompletedParams = {
  scenario: 'combine_subscription' | 'subscription_management'
  action: 'add' | 'remove' | 'update'
  subscriptionId: string
  cartItem: CartItemType
  itemCount: number
  customerType: CustomerType
  currencyCode: string
}

export const TrackAddSubscriptionCompleted = ({
  scenario,
  action,
  subscriptionId,
  cartItem,
  itemCount,
  customerType,
  currencyCode,
}: TrackSubscriptionCompletedParams) =>
  mixpanelService.trackEvent(MixpanelEvent.SUBSCRIPTION_ITEMS_AMOUNT_CHANGES, {
    scenario,
    action,
    subscription_id: subscriptionId,
    item_change_amount: itemCount,
    item_sku: cartItem.product.sku,
    item_name: cartItem.title,
    item_variant: cartItem.product.combinationName,
    currency: currencyCode,
    order_total:
      getTerms({
        forCustomerType: customerType,
        pricingData: cartItem.product.pricing || null,
      })?.priceEach || 0,
    ...(scenario === 'combine_subscription' && {
      add_to_cart_location: 'product_page_main_button',
    }),
  })
type TrackSubscriptionQuantityChangesParams = {
  scenario: 'subscription_management'
  action: 'add' | 'remove'
  editedSubscription: HydraLinesItem
  subscription: HydraGetSubscriptionItem
  variation: CosmicVariationType
  quantity: number
}

export const TrackSubscriptionQuantityChanges = ({
  scenario,
  action,
  subscription,
  editedSubscription,
  variation,
  quantity,
}: TrackSubscriptionQuantityChangesParams) =>
  mixpanelService.trackEvent(MixpanelEvent.SUBSCRIPTION_ITEMS_AMOUNT_CHANGES, {
    scenario,
    action,
    subscription_id: subscription.id.unicity,
    item_change_amount: quantity,
    item_sku: variation.sku,
    item_name: editedSubscription.productData?.name || '',
    item_variant: variation.combinationName,
    value: subscription.terms.subtotal,
  })

type TrackFlavorChangeType = {
  subscription: HydraGetSubscriptionItem
  oldVariant: CosmicVariationType
  newVariant: CosmicVariationType
}

export const TrackFlavorChange = ({
  subscription,
  oldVariant,
  newVariant,
}: TrackFlavorChangeType) =>
  mixpanelService
    .trackEvent(MixpanelEvent.SUBSCRIPTION_FLAVOR_CHANGED, {
      subscriptionId: subscription.id.unicity,
      item_sku: newVariant.sku,
      old_value: oldVariant.combinationName,
      new_value: newVariant.combinationName,
    })
    .catch(() => {})
